// A carousel of images for the space page

import 'swiper/css';
import 'swiper/css/navigation';

import React, { useCallback, useLayoutEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Navigation } from 'swiper/modules';
import ImgsViewer from './Common/ImagesViewer/ImagesViewer';
import Spinner from './Spinner';

type ImagesCarouselProps = {
  images: {
    url: string;
    alt: string;
  }[];
};

export default function ImagesCarousel({ images = [] }: ImagesCarouselProps) {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);

  const handleImageClick = (key: number) => () => {
    console.log('key', key);
    setImageViewerOpen(true);
    setCurrentImage(key);
  };

  const handleCloseViewer = () => {
    setImageViewerOpen(false);
  };

  const handleNext = useCallback(() => {
    if (currentImage === images.length - 1) {
      setCurrentImage(0);
      return;
    }
    setCurrentImage(currentImage + 1);
  }, [currentImage, images.length]);

  const handlePrevious = useCallback(() => {
    if (currentImage === 0) {
      setCurrentImage(images.length - 1);
      return;
    }
    setCurrentImage(currentImage - 1);
  }, [currentImage, images.length]);

  // add touch event listener to image viewer
  // depending on the direction of the swipe, go to next or previous image
  useLayoutEffect(() => {
    if (imageViewerOpen) {
      const handleTouchStart = (e: TouchEvent) => {
        const x = e.touches[0].clientX;
        const y = e.touches[0].clientY;
        const touchStart = { x, y };
        const handleTouchMove = (e: TouchEvent) => {
          const x = e.touches[0].clientX;
          const y = e.touches[0].clientY;
          const touchEnd = { x, y };
          const dx = touchEnd.x - touchStart.x;
          const dy = touchEnd.y - touchStart.y;
          if (Math.abs(dx) > Math.abs(dy)) {
            if (dx > 0) {
              handlePrevious();
            } else {
              handleNext();
            }
          }
        };
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', () => {
          document.removeEventListener('touchmove', handleTouchMove);
        });
      };
      document.addEventListener('touchstart', handleTouchStart);
      return () => {
        document.removeEventListener('touchstart', handleTouchStart);
      };
    }
  }, [imageViewerOpen, currentImage, handlePrevious, handleNext]);

  return (
    <div className="relative overflow-hidden w-full">
      <Swiper
        cssMode
        navigation={true}
        modules={[Navigation]}
        className="highlight-carousel"
        slidesPerView="auto"
        spaceBetween={0}
      >
        {images.map((image, key) => (
          <SwiperSlide key={key}>
            <div
              className={
                key === 0
                  ? 'pl-4 pr-2 overflow-visible'
                  : 'px-2 overflow-visible'
              }
            >
              <div
                className="overflow-hidden rounded-lg h-[240px] w-[240px]"
                style={{
                  width: '240px',
                  height: '240px',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundImage: `url(${image.url})`,
                }}
                onClick={handleImageClick(key)}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      <ImgsViewer
        imgs={images.map((i) => ({ src: i.url, alt: i.alt }))}
        currImg={currentImage}
        isOpen={imageViewerOpen}
        onClickPrev={handlePrevious}
        onClickNext={handleNext}
        onClose={handleCloseViewer}
      />
    </div>
  );
}
